/*
 * Insert Flip-Through
 * - Template list comes from the "Insert Flipbook" metaobjects (JSON in the section).
 * - Each PDF is rendered to page images in the browser with pdf.js, then shown
 *   with StPageFlip (page-flip). Both libraries load lazily from theme assets.
 * - Book order: kraft cover, kraft inside cover, then paper pages. Paper pages
 *   are the intro pages once, then the rest of the PDF repeated "repeat" times.
 *   A blank paper page 1 is added when needed so two-page spreads land
 *   left/right like the printed insert.
 */
(() => {
  if (customElements.get('insert-flipbook')) return;

  const SIZE_ORDER = ['A5', 'B5', 'A6']; // same order as the product's size picker
  // Relative book sizes (not to scale on purpose: keeps A6 readable).
  const SIZE_SCALE = { B5: 1, A5: 0.85, A6: 0.7 };
  const SIZE_INFO = {
    A5: { name: 'A5 Standard', dims: '148 × 210 mm' },
    B5: { name: 'B5 Expanded', dims: '176 × 250 mm' },
    A6: { name: 'A6 Pocket', dims: '105 × 148 mm' },
  };
  const PAPER = '#ffffff';
  const PT_PER_MM = 72 / 25.4;
  const COVER_PAGES = 2; // front cover + inside front cover
  const DEFAULT_OPEN_PAGE = 2; // paper page shown first unless the metaobject says otherwise
  const RENDER_TIERS = [600, 900, 1200, 1500];
  const PORTRAIT_BELOW = 640; // stage width (px) under which one page shows at a time
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- shared caches (survive section re-renders in the editor) ---------- */
  const shared = (window.__insertFlipbook = window.__insertFlipbook || {
    scripts: {},
    docs: new Map(),
    images: new Map(),
  });

  function loadScript(src) {
    if (!shared.scripts[src]) {
      shared.scripts[src] = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = resolve;
        s.onerror = () => {
          delete shared.scripts[src];
          reject(new Error('Could not load ' + src));
        };
        document.head.appendChild(s);
      });
    }
    return shared.scripts[src];
  }

  function absUrl(url) {
    if (!url) return null;
    return url.startsWith('//') ? 'https:' + url : url;
  }

  function getDoc(url) {
    if (!shared.docs.has(url)) {
      // isEvalSupported:false closes the font-eval path in pdf.js 3.x.
      const p = window.pdfjsLib.getDocument({ url, isEvalSupported: false }).promise;
      shared.docs.set(url, p);
      p.catch(() => shared.docs.delete(url));
    }
    return shared.docs.get(url);
  }

  // Renders one PDF page to an image. marginMm shrinks the design evenly so a
  // white border of that width shows on the sides, like the printed insert.
  function renderPage(doc, url, pageNum, width, marginMm) {
    const key = [url, pageNum, width, marginMm].join('|');
    if (!shared.images.has(key)) {
      const p = (async () => {
        const page = await doc.getPage(pageNum);
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: width / base.width });
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        const ctx = canvas.getContext('2d', { alpha: false });
        const params = { canvasContext: ctx, viewport, background: PAPER };
        const marginPt = Math.max(0, marginMm || 0) * PT_PER_MM;
        if (marginPt > 0) {
          const k = (base.width - 2 * marginPt) / base.width;
          params.transform = [k, 0, 0, k, (canvas.width * (1 - k)) / 2, (canvas.height * (1 - k)) / 2];
        }
        await page.render(params).promise;
        const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.9));
        canvas.width = canvas.height = 0; // free canvas memory on iOS
        return URL.createObjectURL(blob);
      })();
      shared.images.set(key, p);
      p.catch(() => shared.images.delete(key));
    }
    return shared.images.get(key);
  }

  // Book pages in order: 'cover', 'inside', then PDF page numbers ('blank' = empty paper page).
  // blankSetting: 'Auto' (add only when needed), 'Yes' (always) or 'No' (never).
  function buildSequence(numPages, intro, repeat, blankSetting) {
    const introCount = Math.max(0, Math.min(parseInt(intro, 10) || 0, numPages - 1));
    const times = Math.max(1, parseInt(repeat, 10) || 1);
    const introPages = [];
    const unit = [];
    for (let i = 1; i <= numPages; i++) (i <= introCount ? introPages : unit).push(i);

    // Paper page 1 is a right-hand page, so even-numbered paper pages are on
    // the left. A two-page (even) design must start on an even paper page.
    const setting = String(blankSetting || 'Auto').toLowerCase();
    const blank = setting === 'yes' || (setting !== 'no' && unit.length % 2 === 0 && introCount % 2 === 0);
    const paper = blank ? ['blank'] : [];
    paper.push(...introPages);
    for (let r = 0; r < times; r++) paper.push(...unit);
    return { seq: ['cover', 'inside', ...paper], paperCount: paper.length };
  }

  class InsertFlipbook extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._token = 0;

      let data = [];
      try {
        data = JSON.parse(this.querySelector('.ifb__data').textContent || '[]');
      } catch (e) {
        console.error('[insert-flipbook] bad data', e);
      }
      this.templates = data
        .map((t) => ({
          ...t,
          margin: parseFloat(t.margin) || 0,
          pdf: { A5: absUrl(t.pdf && t.pdf.A5), B5: absUrl(t.pdf && t.pdf.B5), A6: absUrl(t.pdf && t.pdf.A6) },
        }))
        .filter((t) => t.name && SIZE_ORDER.some((s) => t.pdf[s]))
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || a.name.localeCompare(b.name));

      if (!this.templates.length) {
        this.hidden = true;
        return;
      }

      this.els = {
        chips: this.querySelector('.ifb__templates'),
        select: this.querySelector('.ifb__select'),
        sizes: [...this.querySelectorAll('.ifb__size')],
        frame: this.querySelector('.ifb__frame'),
        book: this.querySelector('.ifb__book'),
        error: this.querySelector('.ifb__error'),
        prev: [...this.querySelectorAll('.ifb__arrow--prev')],
        next: [...this.querySelectorAll('.ifb__arrow--next')],
        counter: this.querySelector('.ifb__counter'),
        sample: this.querySelector('.ifb__sample'),
        sizeNote: this.querySelector('.ifb__size-note'),
      };
      this.fullPages = {
        A5: parseInt(this.dataset.pagesA5, 10) || 60,
        B5: parseInt(this.dataset.pagesB5, 10) || 60,
        A6: parseInt(this.dataset.pagesA6, 10) || 40,
      };

      // Starting size, then starting template within that size.
      const sizes = SIZE_ORDER.filter((s) => this.templates.some((t) => t.pdf[s]));
      this.size = sizes.includes(this.dataset.defaultSize) ? this.dataset.defaultSize : sizes[0];
      const wanted = (this.dataset.defaultTemplate || '').trim().toLowerCase();
      const avail = this.available();
      this.current = avail.find((t) => t.name.toLowerCase() === wanted || t.handle === wanted) || avail[0];
      this.portrait = this.isPortraitStage();

      this.renderTemplateControls();
      this.syncControls();
      this.applyScale(false);
      this.bindEvents();

      // Load the libraries and first PDF only when the section is near view.
      this._io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this._io.disconnect();
            this.boot();
          }
        },
        { rootMargin: '600px 0px' }
      );
      this._io.observe(this);
    }

    disconnectedCallback() {
      this._token++;
      if (this._io) this._io.disconnect();
      if (this._ro) this._ro.disconnect();
      if (this._fro) this._fro.disconnect();
      this.destroyBook();
      this._connected = false;
    }

    available(size = this.size) {
      return this.templates.filter((t) => t.pdf[size]);
    }

    /* ---------- setup ---------- */
    // Chips (desktop) and a dropdown (phones) are built from the same list.
    renderTemplateControls() {
      const frag = document.createDocumentFragment();
      this.templates.forEach((t, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ifb__chip';
        b.textContent = t.name;
        b.dataset.index = String(i);
        frag.appendChild(b);
      });
      this.els.chips.replaceChildren(frag);
    }

    bindEvents() {
      this.els.chips.addEventListener('click', (e) => {
        const b = e.target.closest('.ifb__chip');
        if (b) this.selectTemplate(this.templates[Number(b.dataset.index)]);
      });
      this.els.select.addEventListener('change', () => {
        this.selectTemplate(this.templates[Number(this.els.select.value)]);
      });
      this.els.sizes.forEach((b) => b.addEventListener('click', () => this.selectSize(b.dataset.size)));
      this.els.prev.forEach((b) => b.addEventListener('click', () => this.pf && this.pf.flipPrev()));
      this.els.next.forEach((b) => b.addEventListener('click', () => this.pf && this.pf.flipNext()));
      this.els.frame.addEventListener('keydown', (e) => {
        if (!this.pf) return;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.pf.flipPrev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.pf.flipNext();
        }
      });

      // Switch between one-page (phones) and two-page (wider) layouts.
      this._ro = new ResizeObserver(() => {
        const portrait = this.isPortraitStage();
        if (portrait !== this.portrait) {
          this.portrait = portrait;
          this.style.setProperty('--ifb-pages', portrait ? 1 : 2);
          if (this.pf) this.load({ keepPage: true });
        }
      });
      this._ro.observe(this.querySelector('.ifb__stage'));
      this.style.setProperty('--ifb-pages', this.portrait ? 1 : 2);

      // Re-measure the book whenever its frame changes size (size switches,
      // the page shape settling on first load, fonts loading, etc.).
      this._fro = new ResizeObserver(() => {
        if (!this.pf || this._froQueued) return;
        this._froQueued = true;
        requestAnimationFrame(() => {
          this._froQueued = false;
          if (!this.pf) return;
          try {
            this.pf.getUI().update();
          } catch (e) {
            /* book was rebuilt mid-frame */
          }
        });
      });
      this._fro.observe(this.els.frame);
    }

    async boot() {
      this.setBusy(true);
      try {
        await Promise.all([loadScript(this.dataset.pageflipSrc), loadScript(this.dataset.pdfjsSrc)]);
        if (!window.pdfjsLib || !window.St) throw new Error('Flipbook libraries missing');
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = this.dataset.pdfjsWorkerSrc;
        this.libsReady = true;
        this.load({ keepPage: false });
      } catch (e) {
        console.error('[insert-flipbook]', e);
        this.showError();
      }
    }

    isPortraitStage() {
      const stage = this.querySelector('.ifb__stage');
      return (stage ? stage.clientWidth : window.innerWidth) < PORTRAIT_BELOW;
    }

    /* ---------- user actions ---------- */
    selectTemplate(t) {
      if (!t || t === this.current || !t.pdf[this.size]) return;
      this.current = t;
      this.syncControls();
      this.scrollChipIntoView();
      if (this.libsReady) this.load({ keepPage: false });
    }

    selectSize(size) {
      if (size === this.size || !this.available(size).length) return;
      this.size = size;
      // Templates not made in this size are hidden, so fall back to the first one that is.
      const sameTemplate = !!this.current.pdf[size];
      if (!sameTemplate) this.current = this.available()[0];
      this.syncControls();
      this.applyScale(true);
      this.scrollChipIntoView();
      if (this.libsReady) this.load({ keepPage: sameTemplate });
    }

    scrollChipIntoView() {
      const row = this.els.chips;
      const chip = row.querySelector('[aria-pressed="true"]');
      if (!chip || row.scrollWidth <= row.clientWidth) return;
      const left = chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2;
      row.scrollTo({ left, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
    }

    syncControls() {
      this.els.sizes.forEach((b) => {
        const s = b.dataset.size;
        b.disabled = !this.available(s).length;
        b.title = SIZE_INFO[s].dims;
        b.setAttribute('aria-pressed', String(s === this.size));
      });

      this.els.chips.querySelectorAll('.ifb__chip').forEach((b) => {
        const t = this.templates[Number(b.dataset.index)];
        b.hidden = !t.pdf[this.size];
        b.setAttribute('aria-pressed', String(t === this.current));
      });

      const options = this.available().map((t) => {
        const o = document.createElement('option');
        o.value = String(this.templates.indexOf(t));
        o.textContent = t.name;
        o.selected = t === this.current;
        return o;
      });
      this.els.select.replaceChildren(...options);

      const info = SIZE_INFO[this.size];
      this.els.sizeNote.textContent = `${info.name} · ${info.dims}`;
    }

    // Grow or shrink the book to the selected size. The frame ResizeObserver
    // keeps the current book in sync while the width animates.
    applyScale(animate) {
      const frame = this.els.frame;
      if (animate && this.pf && !REDUCED_MOTION) {
        frame.classList.add('is-resizing');
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => frame.classList.remove('is-resizing'), 650);
        this._scaleDone = new Promise((res) => setTimeout(res, 620));
      }
      frame.style.setProperty('--ifb-scale', SIZE_SCALE[this.size] || 1);
    }

    renderWidth() {
      // Widest a single page can appear for this size, times screen density.
      const max = this.els.frame.parentElement.clientWidth || window.innerWidth;
      const pageCss = (this.portrait ? max : max / 2) * (SIZE_SCALE[this.size] || 1);
      const target = pageCss * Math.min(window.devicePixelRatio || 1, 2);
      return RENDER_TIERS.find((w) => w >= target) || RENDER_TIERS[RENDER_TIERS.length - 1];
    }

    /* ---------- loading + building ---------- */
    async load({ keepPage }) {
      const token = ++this._token;
      const t = this.current;
      const size = this.size;
      const url = t.pdf[size];
      this.setBusy(true);
      this.els.error.hidden = true;
      try {
        const doc = await getDoc(url);
        if (token !== this._token) return;
        const { seq, paperCount } = buildSequence(doc.numPages, t.intro, t.repeat, t.blank);
        const first = await doc.getPage(1);
        const vp = first.getViewport({ scale: 1 });
        const ratio = vp.height / vp.width;
        const width = this.renderWidth();
        const unique = [...new Set(seq.filter((p) => typeof p === 'number'))];
        const images = {};
        await Promise.all(
          unique.map(async (p) => {
            images[p] = await renderPage(doc, url, p, width, t.margin);
          })
        );
        if (token !== this._token) return;
        if (this._scaleDone) await this._scaleDone;
        if (token !== this._token) return;

        // Paper page N sits at book index N + 1 (after the two cover pages).
        const openPage = parseInt(t.open, 10) || DEFAULT_OPEN_PAGE;
        let start = openPage + COVER_PAGES - 1;
        if (keepPage && this.pf) start = this.pf.getCurrentPageIndex();
        start = Math.max(0, Math.min(start, seq.length - 1));
        this.build(seq, images, ratio, start, paperCount);
      } catch (e) {
        if (token === this._token) {
          console.error('[insert-flipbook]', e);
          this.showError();
        }
      } finally {
        if (token === this._token) this.setBusy(false);
      }
    }

    build(seq, images, ratio, startPage, paperCount) {
      const holder = document.createElement('div');
      holder.className = 'ifb__pf';
      seq.forEach((p) => {
        const page = document.createElement('div');
        page.className = 'ifb__page';
        page.dataset.density = 'soft';
        if (p === 'cover' || p === 'inside') {
          page.classList.add('ifb__page--kraft', 'ifb__page--' + p);
          page.dataset.density = 'hard';
        } else if (p === 'blank') {
          page.classList.add('ifb__page--blank');
        } else {
          const img = document.createElement('img');
          img.src = images[p];
          img.alt = '';
          img.draggable = false;
          page.appendChild(img);
        }
        holder.appendChild(page);
      });

      this.style.setProperty('--ifb-ratio', ratio.toFixed(4));
      this.els.book.appendChild(holder);
      this.destroyBook();

      const portrait = this.portrait;
      const pf = new window.St.PageFlip(holder, {
        width: 400,
        height: Math.round(400 * ratio),
        size: 'stretch',
        // minWidth decides one-page vs two-page; we decide it from the stage
        // width so switching sizes never flips the layout.
        minWidth: portrait ? 100000 : 40,
        maxWidth: 4000,
        minHeight: 40,
        maxHeight: 6000,
        autoSize: true,
        usePortrait: true,
        showCover: true,
        startPage,
        drawShadow: true,
        maxShadowOpacity: 0.35,
        flippingTime: REDUCED_MOTION ? 300 : 800,
        mobileScrollSupport: true,
        showPageCorners: true,
        swipeDistance: 30,
      });
      // StPageFlip writes min sizes from the settings onto the element; undo
      // that so the CSS frame controls the size.
      pf.loadFromHTML(holder.querySelectorAll('.ifb__page'));
      holder.style.minWidth = '';
      holder.style.minHeight = '';
      pf.update();

      // The kraft cover turns like card stock; a single last paper page stays soft.
      const last = pf.getPageCount() - 1;
      if (last >= COVER_PAGES) {
        const page = pf.getPage(last);
        if (page && page.setDensity) page.setDensity('soft');
      }

      pf.on('flip', () => this.updateNav());
      pf.on('changeOrientation', () => this.updateNav());
      pf.on('changeState', (e) => {
        this.els.frame.classList.toggle('is-flipping', e.data !== 'read');
      });

      this.pf = pf;
      this.paperCount = paperCount;
      this.els.frame.classList.add('is-ready');
      this.updateNav();
    }

    destroyBook() {
      if (!this.pf) return;
      try {
        this.pf.destroy();
      } catch (e) {
        /* already gone */
      }
      this.pf = null;
    }

    /* ---------- UI state ---------- */
    updateNav() {
      const pf = this.pf;
      if (!pf) return;
      const count = pf.getPageCount();
      const idx = pf.getCurrentPageIndex();
      const portrait = pf.getOrientation() === 'portrait';
      let visible;
      if (portrait) visible = [idx];
      else if (idx === 0) visible = [0];
      else {
        const left = idx % 2 === 1 ? idx : idx - 1;
        visible = left + 1 < count ? [left, left + 1] : [left];
      }

      // Book index -> label: 0 cover, 1 inside cover, then paper pages 1..n.
      const paper = visible.filter((i) => i >= COVER_PAGES).map((i) => i - COVER_PAGES + 1);
      let label;
      if (visible.includes(0)) label = 'Cover';
      else if (paper.length === 2) label = `Pages ${paper[0]}–${paper[1]}`;
      else if (paper.length === 1) label = visible.includes(1) ? `Inside cover · Page ${paper[0]}` : `Page ${paper[0]}`;
      else label = 'Inside cover';
      this.els.counter.textContent = label;

      const full = this.fullPages[this.size];
      this.els.sample.textContent = `Showing ${this.paperCount} of the ${full} pages in each ${this.size} insert`;

      const atStart = visible.includes(0);
      const atEnd = visible.includes(count - 1);
      this.els.prev.forEach((b) => (b.disabled = atStart));
      this.els.next.forEach((b) => (b.disabled = atEnd));
      this.els.frame.classList.toggle('is-landscape', !portrait);
      this.els.frame.classList.toggle('is-single', !portrait && visible.length === 1);
    }

    setBusy(busy) {
      this.els.frame.classList.toggle('is-busy', busy);
      this.els.frame.setAttribute('aria-busy', String(busy));
    }

    showError() {
      this.setBusy(false);
      this.els.error.hidden = false;
    }
  }

  customElements.define('insert-flipbook', InsertFlipbook);
})();
